import { useState, useMemo } from "react";
import { Check, ChevronsUpDown, Ship } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { useEmbarques } from "@/hooks/useEmbarques";
import { useClientes } from "@/hooks/useClientes";

interface EmbarqueComboboxProps {
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

/** TSK-204: selector de embarque destino (solo embarques NO liquidados). */
export function EmbarqueCombobox({
  value,
  onChange,
  placeholder = "Buscar embarque…",
  disabled,
}: EmbarqueComboboxProps) {
  const [open, setOpen] = useState(false);
  const { data: embarques = [], isLoading } = useEmbarques();
  const { data: clientes = [] } = useClientes();

  const clienteMap = useMemo(
    () => Object.fromEntries(clientes.map((c) => [c.id, c.nombre])),
    [clientes]
  );

  const openEmbarques = useMemo(
    () => embarques.filter((e) => !e.liquidado),
    [embarques]
  );

  const selected = openEmbarques.find((e) => e.id === value) ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className="w-full justify-between font-normal"
        >
          {selected ? (
            <span className="flex items-center gap-2 truncate">
              <Ship className="h-3.5 w-3.5 text-primary shrink-0" />
              <span className="font-medium truncate">{selected.numero_do}</span>
              <span className="text-muted-foreground text-xs truncate">
                · {clienteMap[selected.cliente_id ?? ""] ?? "Sin cliente"}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        <Command>
          <CommandInput placeholder="DO, cliente, proveedor…" />
          <CommandList>
            <CommandEmpty>No hay embarques abiertos.</CommandEmpty>
            <CommandGroup heading={`${openEmbarques.length} embarques activos`}>
              {openEmbarques.map((e) => {
                const clienteName = clienteMap[e.cliente_id ?? ""] ?? "—";
                const eta = e.fecha_llegada_estimada
                  ? new Date(e.fecha_llegada_estimada).toLocaleDateString("es-CO", {
                      day: "2-digit",
                      month: "short",
                    })
                  : "Sin ETA";
                return (
                  <CommandItem
                    key={e.id}
                    value={`${e.numero_do} ${clienteName} ${e.proveedor ?? ""}`}
                    onSelect={() => {
                      onChange(e.id === value ? null : e.id);
                      setOpen(false);
                    }}
                    className="flex items-start gap-2 py-2"
                  >
                    <Check
                      className={cn(
                        "mt-0.5 h-4 w-4 shrink-0",
                        value === e.id ? "opacity-100 text-primary" : "opacity-0"
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{e.numero_do}</span>
                        <Badge
                          variant="outline"
                          className="h-4 text-[10px] px-1.5 border-primary/30 text-primary"
                        >
                          {e.estado_logistico}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {clienteName} · {e.proveedor ?? "Sin proveedor"} · ETA {eta}
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
